import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { BookButton, BookModal, BookTexField } from '../../components';
import { ChapterContentValidation } from '../ChapterContents/validation';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CreateChapterContent, UpdateChapterContent } from "../../DAL/ChaptersContents/contents";
import { enqueueSnackbar } from "notistack";
import { Box, Grid, Typography } from '@mui/material';
import JoditTextEditor from '../../components/text-editor/JoditTextEditor';

const ContentModal = ({
  open,
  setOpen,
  action,
  singleContent,
  tableCells,
  handleGetContentData,
  chapterData,
  bookData,
  handleBookGetChapter,
  selectedcontentBookId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.up('sm'));

  const handleAddContent = async (values) => {
    setIsLoading(true);
    const data = {
      contentBookId: selectedcontentBookId,
      chapterId: values?.chapterId,
      content: values?.content,
      pageNo: values?.pageNo,
      url: values?.url
    };
    const response = await CreateChapterContent(data);
    if (response.statusCode == 200) {
      setIsLoading(false);
      handleGetContentData()
      setOpen(false);
      formik.handleReset();
    } else {
      setIsLoading(false);
      if (typeof response.message == "string") {
        enqueueSnackbar(response.message, { variant: "error" });
      } else {
        enqueueSnackbar("Invalid credentials", { variant: "error" });
      }
    }
  };

  const handleUpdateContent = async (values) => {
    setIsLoading(true);
    const data = {
      chapterId: selectedcontentBookId,
      content: values?.content,
      pageNo: values?.pageNo,
      url: values?.url
    };
    const response = await UpdateChapterContent(values?.id, data);
    if (response.statusCode == 200) {
      setIsLoading(false);
      handleGetContentData()
      setOpen(false);
      formik.handleReset();
    } else {
      setIsLoading(false);
      if (typeof response.message == "string") {
        enqueueSnackbar(response.message, { variant: "error" });
      } else {
        enqueueSnackbar("Invalid credentials", { variant: "error" });
      }
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id:
        action !== 'add' && singleContent && singleContent.id
          ? singleContent.id
          : '',
      contentBookId:
        action !== 'add' && singleContent && singleContent.contentBookId
          ? singleContent.contentBookId
          : '',
      chapterId:
        action !== 'add' && singleContent && singleContent.chapterId
          ? singleContent.chapterId
          : '',
      pageNo:
        action !== 'add' && singleContent && singleContent.pageNo
          ? singleContent.pageNo
          : '',
      content:
        action !== 'add' && singleContent && singleContent.content
          ? singleContent.content
          : '',
      url:
        action !== 'add' && singleContent && singleContent.url
          ? singleContent.url
          : '',
    },
    validationSchema: ChapterContentValidation,
    onSubmit: values => {
      setIsLoading(true);
      if (action === 'add') {
        handleAddContent(values);
      } else {
        handleUpdateContent(values);
      }
    },
  });

  const handleInputBook = (e, formik) => {
    formik.setFieldValue('contentBookId', e.target.value)
    handleBookGetChapter(e)
  }

  return (
    <BookModal
      open={open}
      formik={formik}
      setOpen={setOpen}
      title='Content'
      subtitle={`${action === 'add' ? 'Add' : 'Edit'} Content`}
      fullWidth
      maxWidth="xl" // or remove maxWidth to unrestrict
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '100%', // force full screen width
          height: '100vh',   // optional: almost full screen height
          m: 0,             // remove margins
          p: 2              // optional: adjust internal padding
        },
      }}
    >
      <Grid container spacing={2}>
        {/* Left side inputs (excluding content) */}
        <Grid item xs={12} md={3}>
          {tableCells &&
            tableCells.map((t) => {
              if (t.id === 'actions' || t.id === 'content' || t.id === 'bookNoContent' || t.id === 'chapterNoContent') return null;

              const isBook = t.id === 'contentBookId';
              const isChapter = t.id === 'chapterId';

              const select = isBook || isChapter;
              const selectOptions = isBook
                ? bookData
                : isChapter
                  ? chapterData
                  : undefined;

              const onChange = isBook ? handleInputBook : undefined;

              return (
                <BookTexField
                  key={t.id + 'cell'}
                  formik={formik}
                  name={t.id}
                  label={t.title}
                  placeholder={`Enter ${t.title}`}
                  select={select}
                  selectOptions={selectOptions}
                  onChange={onChange}
                />
              );
            })}
        </Grid>

        {/* Right side: Only content textarea */}
        <Grid item xs={12} md={9}>
          {tableCells &&
            tableCells.map(
              t =>
                t.id === 'content' && (
                  <Box sx={{ mb: 4 }}>
                    <Typography sx={{ mb: 1 }}>{t.title}</Typography>
                    <JoditTextEditor
                      value={formik.values[t.id] || ''}
                      handleChange={(value) => {
                        const newValue = value === '<p><br></p>' ? '' : value;
                        formik.setFieldValue(t.id, newValue);
                      }}
                      config={{
                        height: 450,
                        direction: 'rtl',
                        language: 'ur',
                        iframe: true, // Optional: isolates styles
                        events: {
                          afterInit: (editor) => {
                            const editorBody = editor.editor;
                            editorBody.style.fontFamily = "'Jameel Noori Nastaleeq', serif";
                            editorBody.style.direction = "rtl";
                            editorBody.style.textAlign = "right";
                            editorBody.style.fontSize = "18px";

                            // Style tag for deep styling
                            const style = document.createElement("style");
                            style.innerHTML = `
                              * {
                                font-family: 'Jameel Noori Nastaleeq', serif !important;
                                direction: rtl;
                                text-align: right;
                                font-size: 18px;
                              }

                              span[dir="ltr"], a[dir="ltr"] {
                                direction: ltr !important;
                                text-align: left !important;
                                display: inline-block;
                              }
                            `;
                            editor.editorDocument.head.appendChild(style);
                          }
                        }
                      }}
                    />

                    {formik.touched[t.id] && formik.errors[t.id] && (
                      <Typography color="error" variant="caption">
                        {formik.errors[t.id]}
                      </Typography>
                    )}
                  </Box>
                )
            )}

        </Grid>
        <Grid item xs={12} md={8}></Grid>
        <Grid item xs={12} md={4}>
          <BookButton
            onSubmit={formik.handleSubmit}
            title={`${action === 'add' ? 'Add' : 'Update'}`}
            type='primary'
            variant='contained'
            isLoading={isLoading}
            sx={{
              top: '0.625rem',
              marginBottom: '0.625rem'
            }}
          />
        </Grid>
      </Grid>

    </BookModal>
  );
};

export default ContentModal;

